import type BaseEnumType from "@/app/services/baseEnumType";
import type Guid from "@/app/types/common/guid";
import userStore from "@/store/modules/userStore";
import {
  createJsonParseReviver,
  createJsonStringifyReplacer,
} from "@/app/storageReplacerReviverUtils";
import type { LocalDate } from "@js-joda/core";
import router from "@/router/router";
import { watch } from "vue";
import { makeRef } from "@/components/composables/reactivityTypes";
import { Guids } from "@/app/types/common/guids";
import { isNullish } from "@/app/apiutils";

export type BmUiStateTypes =
  | LocalDate
  | LocalDate[]
  | BaseEnumType[]
  | BaseEnumType
  | boolean
  | Guid[]
  | Guid
  | string
  | string[]
  | object
  | undefined;

type BmUiStateValue = BmUiStateTypes | Readonly<BmUiStateTypes>;

export interface BmUiState {
  [key: string]: BmUiStateValue;
}

export class BmUiStateRepository {
  public static load<T extends BmUiState>(key: string): Partial<T> {
    const item =
      localStorage.getItem(
        BmUiStateRepository.calculateStorageKeyForRequest()
      ) ?? "{}";
    const storage = JSON.parse(item, createJsonParseReviver());
    return storage[key] ?? {};
  }

  public static save<T extends BmUiState>(key: string, state: T): void {
    const storage = JSON.parse(
      localStorage.getItem(
        BmUiStateRepository.calculateStorageKeyForRequest()
      ) ?? "{}"
    );
    storage[key] = state;
    localStorage.setItem(
      BmUiStateRepository.calculateStorageKeyForRequest(),
      JSON.stringify(storage, createJsonStringifyReplacer())
    );
  }

  public static loadBoolean(
    key: string,
    stateKey: string,
    defaultValue: boolean = false
  ): boolean {
    const value = BmUiStateRepository.load(key)[stateKey];
    return typeof value === "boolean" ? value : defaultValue;
  }

  public static loadGuids(
    key: string,
    stateKey: string,
    validGuids?: readonly Guid[],
    defaultValue: readonly Guid[] = []
  ): readonly Guid[] {
    const value = BmUiStateRepository.load(key)[stateKey];
    const guids = Guids.allAreGuids(value) ? value : defaultValue;
    return validGuids ? Guids.intersect(guids, validGuids) : guids;
  }

  public static loadString(
    key: string,
    stateKey: string,
    defaultValue: string = "",
    validStrings?: readonly string[]
  ): string {
    const value = BmUiStateRepository.load(key)[stateKey];

    return isNullish(value) ||
      typeof value !== "string" ||
      (validStrings && !validStrings.includes(value))
      ? defaultValue
      : value;
  }

  public static saveValue<T extends BmUiStateValue>(
    key: string,
    stateKey: string,
    value: T
  ): void {
    BmUiStateRepository.save(key, {
      ...BmUiStateRepository.load(key),
      [stateKey]: value,
    });
  }

  public static preserve<T extends BmUiState>(
    key: string,
    extract: () => T,
    restore: (state: Partial<T>) => void
  ) {
    watch(
      () => extract(),
      (newState) => BmUiStateRepository.save(key, newState)
    );
    restore(BmUiStateRepository.load(key));
  }

  private static calculateStorageKeyForRequest() {
    return `${userStore.state.user?.id.toString() ?? ""}-bmuistate`.replace(
      /^-/,
      ""
    );
  }

  public static getKeyForCurrentRoute() {
    const currentRouteName = router.currentRoute.name;
    if (!currentRouteName) {
      // eslint-disable-next-line no-console
      console.error(
        "Key requested for current route, when no route is available."
      );
      return "";
    }

    return currentRouteName;
  }
}

export function usePreservedBoolean(
  uiStateKey: string,
  defaultValue: boolean = false
) {
  const content = makeRef<boolean>(defaultValue);
  BmUiStateRepository.preserve(
    uiStateKey,
    () => ({ content: content.value }),
    (state) => (content.value = !!state.content)
  );
  return content;
}
